import React, { useMemo, useState } from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { archetypeByKey } from '../pessoal/jobArchetypes';
import { IsoPerson } from './officePeople';
import { buildOfficeLayout } from './buildOfficeLayout';
import { IsoCanvas } from './IsoCanvas';
import { UI } from '../../ui';

interface IsoOfficeProps {
  people: IsoPerson[];
  /** Semente estável (id da empresa) — cena pixel-idêntica entre sessões. */
  seed: number;
  /** Rótulo do total (ex.: "colaboradores"). */
  unidade?: string;
}

type View = 'cena' | 'dados';

export function IsoOffice({ people, seed, unidade = 'colaboradores' }: IsoOfficeProps) {
  const [view, setView] = useState<View>('cena');
  const model = useMemo(() => buildOfficeLayout(people, seed), [people, seed]);

  const legenda = useMemo(() => {
    const m = new Map<
      string,
      { label: string; color: string; Icon: React.ElementType; qt: number }
    >();
    people.forEach(p => {
      const cur = m.get(p.archetypeKey);
      if (cur) cur.qt += 1;
      else {
        const arq = archetypeByKey(p.archetypeKey);
        m.set(p.archetypeKey, { label: arq.label, color: arq.accent, Icon: arq.Icon, qt: 1 });
      }
    });
    return Array.from(m.values()).sort((a, b) => b.qt - a.qt);
  }, [people]);

  if (people.length === 0) return null;

  return (
    <Box
      position="relative"
      borderRadius="18px"
      overflow="hidden"
      bgGradient="linear(160deg, #EAF2FB 0%, #DCE9F7 55%, #CFE0F2 100%)"
      border="1px solid rgba(255,255,255,0.8)"
      boxShadow="inset 0 1px 0 rgba(255,255,255,0.7), 0 14px 36px -20px rgba(15,23,42,0.4)"
      p={5}
    >
      <Flex justify="space-between" align="center" mb={3} gap={3} wrap="wrap">
        <Flex align="baseline" gap={2}>
          <Text fontSize="26px" fontWeight={900} color={UI.ink} lineHeight="1">
            {model.total}
          </Text>
          <Text
            fontSize="11px"
            fontWeight={700}
            color={UI.muted}
            textTransform="uppercase"
            letterSpacing="0.5px"
          >
            {unidade} · escritório
          </Text>
        </Flex>
        <Flex gap={1} p={1} bg="rgba(255,255,255,0.6)" borderRadius="11px">
          {(['cena', 'dados'] as const).map(v => (
            <Box
              key={v}
              as="button"
              px={3}
              py={1}
              borderRadius="8px"
              fontSize="12px"
              fontWeight={700}
              bg={view === v ? 'white' : 'transparent'}
              color={view === v ? UI.brand : UI.muted}
              boxShadow={view === v ? '0 1px 4px rgba(15,23,42,0.12)' : 'none'}
              onClick={() => setView(v)}
            >
              {v === 'cena' ? 'Cena' : 'Dados'}
            </Box>
          ))}
        </Flex>
      </Flex>

      {view === 'cena' ? (
        <Box position="relative" overflowX="auto" overflowY="hidden" pb={2}>
          <IsoCanvas model={model} />
          {model.overflow > 0 && (
            <Flex
              position="absolute"
              right="10px"
              bottom="10px"
              px={2.5}
              py={1}
              borderRadius="full"
              bg="rgba(255,255,255,0.9)"
              boxShadow="0 4px 12px -4px rgba(15,23,42,0.4)"
              fontSize="12px"
              fontWeight={800}
              color={UI.ink}
            >
              +{model.overflow} não mostrados
            </Flex>
          )}
        </Box>
      ) : (
        <Box bg="rgba(255,255,255,0.6)" borderRadius="12px" p={3}>
          {legenda.map(l => (
            <Flex
              key={l.label}
              align="center"
              justify="space-between"
              py={1.5}
              borderBottom="1px solid rgba(15,23,42,0.06)"
            >
              <Flex align="center" gap={2}>
                <Flex
                  align="center"
                  justify="center"
                  w="20px"
                  h="20px"
                  borderRadius="6px"
                  bg={`${l.color}22`}
                  color={l.color}
                >
                  <Icon as={l.Icon} boxSize="12px" weight="bold" />
                </Flex>
                <Text fontSize="12.5px" color={UI.ink}>
                  {l.label}
                </Text>
              </Flex>
              <Text fontSize="13px" fontWeight={800} color={UI.ink}>
                {l.qt}
              </Text>
            </Flex>
          ))}
        </Box>
      )}

      {view === 'cena' && legenda.length > 0 && (
        <Flex gap={3} wrap="wrap" mt={3}>
          {legenda.slice(0, 8).map(l => (
            <Flex key={l.label} align="center" gap={1.5}>
              <Flex
                align="center"
                justify="center"
                w="16px"
                h="16px"
                borderRadius="5px"
                bg={`${l.color}1F`}
                color={l.color}
              >
                <Icon as={l.Icon} boxSize="10px" weight="bold" />
              </Flex>
              <Text fontSize="11px" color={UI.muted}>
                {l.label} <b>{l.qt}</b>
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  );
}
